import { model as router } from '!/router'
import {
  AuthFormBoxRowGaps,
  AuthFormBoxTemplate,
  ErrorMessage,
  FieldLabelWrapper,
  FIXED_INDENTS,
  InlineBackAngleWithTitle,
  LoginUsernameInputBlock,
  PrimarySubmitButton,
  TextBoxCaption3,
  TextBoxCaption3Secondary,
  TextBoxCaption4Secondary,
  TextBoxHeadline2,
} from '@setplex/arcana'
import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import type { JSX } from 'react'
import {
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
  type SyntheticEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { AuthLayout } from '~/widgets/auth'
import { humanizeFrom, humanizeTo } from '../../../shared/tools'
import {
  $email,
  $from,
  $locked,
  $permError,
  $to,
  $viewError,
  confirmForm,
  verifyResetPasswordCodeFx,
} from '../model'
import { VerifyTextBlock } from './VerifyTextBlock'
import { AppTitle } from '~/shared/ui/components/AppTitle'

export function Title(): JSX.Element {
  return <AppTitle t={['forgot_password', 'account_verify']} />
}

VerifyPage.displayName = 'VerifyPage'
export function VerifyPage(): JSX.Element {
  const { t } = useTranslation()

  const from = useUnit($from) // reset password uuid request time
  const to = useUnit($to) // reset password uuid end of lifetime
  const email = useUnit($email) // reset password email

  const pending = useUnit(verifyResetPasswordCodeFx.pending)
  const errorKey = useUnit($viewError)
  const permanentError = useUnit($permError)
  const locked = useUnit($locked)
  const { submit, fields, eachValid, hasError, errorText } =
    useForm(confirmForm)

  // ticks
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const timeout = setTimeout(() => setTick((tick) => tick + 1), 1000)
    return () => clearTimeout(timeout)
  }, [tick])

  // countdown code expiration
  const [liveTo, setLifeTo] = useState(humanizeTo(to, t))
  useEffect(() => setLifeTo(humanizeTo(to, t)), [to, tick, t])

  // countdown resend request allowance
  const [liveResendIn, setLiveResendIn] = useState(humanizeFrom(from, 180))
  useEffect(() => setLiveResendIn(humanizeFrom(from, 180)), [from, tick])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const onGoBack = (e: SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    router.navigateBack()
  }

  let emailTitle = t('email_code_verify_message', { email })
  if (!email) {
    emailTitle = t('email_code_verify_message', {
      email: t('code_sent_requested_email_web'),
    })
  }

  const codeExpiredTitle =
    liveTo === null
      ? t('code_expired_error')
      : t('email_code_expiration_time_web', {
          time: liveTo,
        })

  return (
    <AuthLayout>
      <AuthFormBoxTemplate
        rowGap={AuthFormBoxRowGaps.SIZE_20}
        padding={[
          FIXED_INDENTS.INDENT_140,
          FIXED_INDENTS.INDENT_140,
          FIXED_INDENTS.INDENT_100,
          FIXED_INDENTS.INDENT_140,
        ]}
      >
        <form
          onSubmit={onSubmit}
          autoComplete='off'
          noValidate
          style={{ marginBottom: '12px' }}
        >
          <a href='/forgot-password' onClick={onGoBack}>
            {/* FIXME: button inside anchor is not valid HTML */}
            <InlineBackAngleWithTitle title={t('back')} />
          </a>
          <TextBoxHeadline2 title={t('account_verify')} />
          <TextBoxCaption3 title={emailTitle} />
          <FieldLabelWrapper>
            <LoginUsernameInputBlock
              value={fields.code.value}
              placeholder={t('enter_email_code')}
              label={t('code')}
              disabled={
                pending || liveTo === null || locked || Boolean(permanentError)
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                /^\d*$/.test(e.target.value) && // do not allow to type anything except numbers
                fields.code.onChange(e.target.value)
              }
              errorMessage={
                hasError('code')
                  ? errorText('code', {
                      required: t('email_code_required_error'),
                      digits: t('email_code_symbols_error'),
                      maxLength: t('email_code_length_error'),
                      minLength: t('email_code_length_error'),
                    })
                  : ''
              }
              autoComplete='off'
            />
            {liveTo !== undefined && (
              <TextBoxCaption3Secondary
                title={locked || permanentError ? '\xA0' : codeExpiredTitle}
              />
            )}
          </FieldLabelWrapper>
          <PrimarySubmitButton
            title={t('verify_continue')}
            disabled={
              pending ||
              !eachValid ||
              liveTo === null ||
              locked ||
              Boolean(permanentError)
            }
            isLoading={pending || locked}
          />
        </form>
        <VerifyTextBlock liveTo={liveTo} liveResendIn={liveResendIn} />
        <TextBoxCaption4Secondary title={t('code_verify_footer')} />
        {errorKey && <ErrorMessage width='100%' title={t(errorKey)} />}
      </AuthFormBoxTemplate>
    </AuthLayout>
  )
}
